/** "My strategies", "My indicators" (Python code) and "Rule strategies" (no code), kept in this browser.
 *  The Python editor adds items, the backtest list shows them; both read and write through here so a
 *  change on one side shows up on the other straight away. */

export const LAB_ITEMS_KEY = "lwt.lab.items";
export const LAB_RULES_KEY = "lwt.lab.rules";
export const AI_BACKTEST_SUMMARY_KEY = "lwt.ai.backtestSummary";
const MAX_ITEMS = 60;
const MAX_CODE = 60000;

const listeners = new Set();
let editListener = null;
let pendingEdit = null;

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw ? JSON.parse(raw) : [];
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or blocked: the change still applies until the page is reloaded
  }
}

let memItems = null;
let memRules = null;

export function newId(prefix) {
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}:${Date.now().toString(36)}${rand}`;
}

/** Keep only well-formed param specs (numbers, short labels). Pure, unit-tested. */
export function cleanParamSpecs(specs) {
  if (!Array.isArray(specs)) return [];
  const seen = new Set();
  const out = [];
  specs.forEach((p) => {
    const key = String(p?.key ?? "").trim().slice(0, 30);
    const value = Number(p?.value);
    if (!key || seen.has(key) || !Number.isFinite(value)) return;
    seen.add(key);
    const num = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? undefined : Number(v));
    out.push({ key, label: String(p.label || key).slice(0, 30), value, min: num(p.min), max: num(p.max), step: num(p.step) });
  });
  return out.slice(0, 12);
}

/** A stored code item, validated. Returns null for junk. Pure, unit-tested. */
export function cleanItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const kind = raw.kind === "indicator" ? "indicator" : raw.kind === "strategy" ? "strategy" : null;
  const code = typeof raw.code === "string" ? raw.code.slice(0, MAX_CODE) : "";
  const name = String(raw.name || "").trim().slice(0, 40);
  if (!kind || !code.trim() || !name || typeof raw.id !== "string") return null;
  const isImportedPlugin = raw.id.startsWith("plugin:");
  const outputs = Array.isArray(raw.outputs) ? raw.outputs.map((o) => String(o).slice(0, 30)).filter(Boolean).slice(0, 8) : [];
  const packages = isImportedPlugin ? [] : Array.isArray(raw.packages) ? raw.packages.map((p) => String(p).trim()).filter(Boolean).slice(0, 12) : [];
  return {
    id: raw.id,
    kind,
    name,
    code,
    params: cleanParamSpecs(raw.params),
    outputs: kind === "indicator" ? (outputs.length ? outputs : ["Value"]) : outputs,
    packages,
    updatedAt: Number(raw.updatedAt) || 0,
  };
}

export function getItems() {
  if (!memItems) memItems = readJson(LAB_ITEMS_KEY).map(cleanItem).filter(Boolean);
  return memItems;
}

export function getRules() {
  if (!memRules) memRules = readJson(LAB_RULES_KEY).filter((r) => r && typeof r.id === "string" && r.name);
  return memRules;
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // a broken listener must not stop the others
    }
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Add or replace a code item (same id = update). Returns the stored item. */
export function saveItem(item) {
  const clean = cleanItem({ ...item, updatedAt: Date.now() });
  if (!clean) throw new Error("This item is missing a name or code.");
  const items = getItems().filter((x) => x.id !== clean.id);
  if (items.length >= MAX_ITEMS) throw new Error(`You can keep up to ${MAX_ITEMS} coded items. Delete one first.`);
  memItems = [...items, clean];
  writeJson(LAB_ITEMS_KEY, memItems);
  emit();
  return clean;
}

export function deleteItem(id) {
  memItems = getItems().filter((x) => x.id !== id);
  writeJson(LAB_ITEMS_KEY, memItems);
  emit();
}

export function saveRule(rule) {
  const clean = { ...rule, name: String(rule.name || "").trim().slice(0, 40) || "Rule strategy", updatedAt: Date.now() };
  memRules = [...getRules().filter((x) => x.id !== clean.id), clean].slice(-MAX_ITEMS);
  writeJson(LAB_RULES_KEY, memRules);
  emit();
  return clean;
}

export function deleteRule(id) {
  memRules = getRules().filter((x) => x.id !== id);
  writeJson(LAB_RULES_KEY, memRules);
  emit();
}

/** The backtest list asks the code editor to open an item. The editor registers one handler;
 *  a request made before the editor exists is kept and delivered when it registers. */
export function requestEdit(id) {
  if (editListener) editListener(id);
  else pendingEdit = id;
}

export function onEditRequest(fn) {
  editListener = fn;
  if (pendingEdit) {
    const id = pendingEdit;
    pendingEdit = null;
    fn(id);
  }
  return () => {
    if (editListener === fn) editListener = null;
  };
}

/** Tests only: forget the in-memory copies so storage is read again. */
export function _resetForTests() {
  memItems = null;
  memRules = null;
  pendingEdit = null;
  editListener = null;
  listeners.clear();
}
